/**
 * JSON Formatter Service
 * Implements business logic for JSON formatting, minification, and validation
 */

import { EncodingOperation, EncoderToolType } from '../types';
import { ProcessingError, ValidationError } from '../utils/errorHandler';
import { CommonValidator } from '../validation/commonValidation';

export type JsonFormatterMode = 'format' | 'minify' | 'validate';

export class JsonFormatterService {
  /**
   * Format JSON with pretty printing
   */
  static async format(
    json: string,
    options: {
      indentSize?: number;
      sortKeys?: boolean;
    } = {}
  ): Promise<EncodingOperation> {
    try {
      // Validate input
      const validation = CommonValidator.validateText(json,
        CommonValidator.getValidationRules(EncoderToolType.JSON_FORMATTER)
      );

      if (!validation.isValid) {
        throw new ValidationError(validation.error!, EncoderToolType.JSON_FORMATTER, validation.suggestion);
      }

      // Parse and validate JSON
      let parsed;
      try {
        parsed = JSON.parse(json);
      } catch (error) {
        throw new ValidationError(
          `Invalid JSON: ${error instanceof Error ? error.message : 'Parse error'}`,
          EncoderToolType.JSON_FORMATTER,
          'Check for missing commas, quotes, or brackets'
        );
      }

      // Sort keys if requested
      if (options.sortKeys) {
        parsed = this.sortObjectKeys(parsed);
      }

      // Format with specified indentation
      const indent = options.indentSize || 2;
      const formatted = JSON.stringify(parsed, null, indent);

      return {
        id: this.generateOperationId(),
        toolType: EncoderToolType.JSON_FORMATTER,
        operationType: 'encode',
        inputData: json,
        outputData: formatted,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          mode: 'format',
          indentSize: indent,
          sortKeys: options.sortKeys || false,
          originalLength: json.length,
          formattedLength: formatted.length
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        'Failed to format JSON',
        EncoderToolType.JSON_FORMATTER,
        'Please check your JSON syntax'
      );
    }
  }

  /**
   * Minify JSON (remove whitespace)
   */
  static async minify(json: string): Promise<EncodingOperation> {
    try {
      // Validate input
      const validation = CommonValidator.validateText(json,
        CommonValidator.getValidationRules(EncoderToolType.JSON_FORMATTER)
      );

      if (!validation.isValid) {
        throw new ValidationError(validation.error!, EncoderToolType.JSON_FORMATTER, validation.suggestion);
      }

      // Parse and validate JSON
      let parsed;
      try {
        parsed = JSON.parse(json);
      } catch (error) {
        throw new ValidationError(
          `Invalid JSON: ${error instanceof Error ? error.message : 'Parse error'}`,
          EncoderToolType.JSON_FORMATTER,
          'Check for missing commas, quotes, or brackets'
        );
      }

      // Minify (no indentation)
      const minified = JSON.stringify(parsed);

      return {
        id: this.generateOperationId(),
        toolType: EncoderToolType.JSON_FORMATTER,
        operationType: 'encode',
        inputData: json,
        outputData: minified,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          mode: 'minify',
          originalLength: json.length,
          minifiedLength: minified.length,
          compressionRatio: ((1 - minified.length / json.length) * 100).toFixed(2) + '%'
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        'Failed to minify JSON',
        EncoderToolType.JSON_FORMATTER,
        'Please check your JSON syntax'
      );
    }
  }

  /**
   * Validate JSON and return validation result
   */
  static async validate(json: string): Promise<EncodingOperation> {
    try {
      // Validate input
      const validation = CommonValidator.validateText(json,
        CommonValidator.getValidationRules(EncoderToolType.JSON_FORMATTER)
      );

      if (!validation.isValid) {
        throw new ValidationError(validation.error!, EncoderToolType.JSON_FORMATTER, validation.suggestion);
      }

      // Try to parse JSON
      let parsed;
      let isValid = true;
      let errorMessage = '';

      try {
        parsed = JSON.parse(json);
      } catch (error) {
        isValid = false;
        errorMessage = error instanceof Error ? error.message : 'Unknown parse error';
      }

      const result = isValid
        ? `Valid JSON\nType: ${Array.isArray(parsed) ? 'Array' : typeof parsed}\n${this.getJsonStats(parsed)}`
        : `Invalid JSON\nError: ${errorMessage}`;

      return {
        id: this.generateOperationId(),
        toolType: EncoderToolType.JSON_FORMATTER,
        operationType: 'decode',
        inputData: json,
        outputData: result,
        timestamp: new Date(),
        status: isValid ? 'success' : 'error',
        errorMessage: isValid ? undefined : errorMessage,
        metadata: {
          mode: 'validate',
          isValid,
          length: json.length
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        'Failed to validate JSON',
        EncoderToolType.JSON_FORMATTER,
        'An unexpected error occurred'
      );
    }
  }

  /**
   * Recursively sort object keys
   */
  private static sortObjectKeys(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map(item => this.sortObjectKeys(item));
    } else if (obj !== null && typeof obj === 'object') {
      const sorted: any = {};
      Object.keys(obj).sort().forEach(key => {
        sorted[key] = this.sortObjectKeys(obj[key]);
      });
      return sorted;
    }
    return obj;
  }

  /**
   * Get JSON statistics
   */
  private static getJsonStats(obj: any): string {
    const stats: string[] = [];

    if (Array.isArray(obj)) {
      stats.push(`Array length: ${obj.length}`);
    } else if (typeof obj === 'object' && obj !== null) {
      stats.push(`Object keys: ${Object.keys(obj).length}`);
      stats.push(`Nested depth: ${this.getDepth(obj)}`);
    }

    return stats.join('\n');
  }

  /**
   * Calculate nesting depth
   */
  private static getDepth(obj: any): number {
    if (typeof obj !== 'object' || obj === null) {
      return 0;
    }

    const depths = Object.values(obj).map(value => this.getDepth(value));
    return depths.length > 0 ? 1 + Math.max(...depths) : 1;
  }

  /**
   * Generate unique operation ID
   */
  private static generateOperationId(): string {
    return `json-formatter-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Clears any cached data (for testing)
   */
  static clearCache(): void {
    // No-op: This service doesn't maintain cache
  }
}
